// src/components/auth/Auth0ProviderWithHistory.jsx
import React, { useEffect } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { auth0Config } from '../../config/auth0.config';
import { setAuth0TokenGetter } from '../../api/client';
import useAuthStore from '../../store/authStore';
import { getRoleFromToken } from '../../utils/jwtUtils';
import { provisionUser } from '../../api/user-service/userService';

/**
 * Auth0 Initializer Component
 * Sets up the token getter and manages auth state
 */
function Auth0Initializer({ children }) {
  const { 
    isAuthenticated, 
    isLoading, 
    user, 
    getAccessTokenSilently,
    logout: auth0Logout 
  } = useAuth0();
  
  const { setUser, setRole, clearAuth, isProvisioned } = useAuthStore();

  // Setup token getter for axios interceptor
  useEffect(() => {
    setAuth0TokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  // Lightweight client->server error reporting so errors land in backend logs (CloudWatch)
  const reportAuthClientError = async (stage, error) => {
    try {
      const message = error?.message || String(error);
      // Reuse an authenticated endpoint so the backend can log to CloudWatch.
      // This is best-effort and should never block auth.
      await provisionUser({
        clientError: {
          stage,
          message,
          status: error?.response?.status,
        },
      });
    } catch {
      // Intentionally swallow any reporting errors
    }
  };

  // Sync Auth0 user with Zustand store
  useEffect(() => {
    const syncAuthState = async () => {
      if (isAuthenticated && user) {
        // If email not verified yet, just wait
        if (!user.email_verified) {
          return;
        }

        // Set user in store
        setUser(user);
        
        // Get token and extract role (already set by Auth0 Action)
        try {
          const token = await getAccessTokenSilently();
          const role = getRoleFromToken(token);
          
          // Store token in localStorage for backward compatibility
          localStorage.setItem('ACCESS_TOKEN', token);

          // Check if there's a pending role from signup
          const pendingRole = localStorage.getItem('pendingUserRole');
          
          // Call backend to provision user in DB
          // If pendingRole exists, send it to override the default USER role
          try {
            const payload = pendingRole ? { roleCode: pendingRole } : {};
            await provisionUser(payload);
            
            // If we sent a pendingRole and it was successful, update local role
            if (pendingRole) {
              setRole(pendingRole);
              localStorage.removeItem('pendingUserRole');
              
              // Force token refresh to get updated role from backend
              const freshToken = await getAccessTokenSilently({ cacheMode: 'off' });
              const updatedRole = getRoleFromToken(freshToken);
              setRole(updatedRole);
              localStorage.setItem('ACCESS_TOKEN', freshToken);
            } else {
              setRole(role);
            }
            
            setUser(user);
          } catch (error) {
            // Ignore 409 Conflict (user already exists)
            if (error.response?.status === 409) {
              setRole(role);
              setUser(user);
            } else {
              await reportAuthClientError('provisionUser', error);
              // Still set user and role even if provision fails
              setRole(role);
              setUser(user);
            }
          }
        } catch (error) {
          await reportAuthClientError('getAccessTokenSilently', error);
        }
      } else if (!isLoading && !isAuthenticated) {
        // User is not authenticated, clear store
        clearAuth();
        localStorage.removeItem('ACCESS_TOKEN');
      }
    };

    syncAuthState();
  }, [isAuthenticated, user, isLoading, getAccessTokenSilently, setUser, setRole, clearAuth]);

  return <>{children}</>;
}

/**
 * Auth0 Provider with React Router History
 * Wraps the app with Auth0Provider and handles redirects
 */
export default function Auth0ProviderWithHistory({ children }) {
  const navigate = useNavigate();

  const onRedirectCallback = (appState) => {
    // After Auth0 callback, navigate to the intended page or home
    const returnTo = appState?.returnTo || window.location.pathname;
    navigate(returnTo);
  };

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      }}
      cacheLocation={auth0Config.cacheLocation}
      useRefreshTokens={auth0Config.useRefreshTokens}
      onRedirectCallback={onRedirectCallback}
    >
      <Auth0Initializer>
        {children}
      </Auth0Initializer>
    </Auth0Provider>
  );
}
